import React, { useState } from 'react';
import { FileText, LinkIcon, AlertTriangle, ChevronDown, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';

/**
 * Zeigt die Grounding-Quellen eines Beitrags an (Anti-Halluzination):
 *   - ausgelesene Dokumente (Name, Zeichen, ob Text gefunden)
 *   - ausgelesene Links (URL, ob erreichbar/verwertbar)
 *   - Warnungen: entfernte erfundene Links + verdächtige Zahlen
 *
 * Props:
 *   sources       = content.grounding_sources  ({documents, links, evidence_snapshot, generated_at})
 *   linkWarnings  = content.link_warnings       (string[] entfernter URLs)
 *   factWarnings  = content.fact_warnings        (string[] verdächtiger Zahlen)
 */
export default function GroundingSourcesPanel({ sources, linkWarnings = [], factWarnings = [] }) {
  const [open, setOpen] = useState(false);
  const [showEvidence, setShowEvidence] = useState(false);

  const documents = (sources && Array.isArray(sources.documents)) ? sources.documents : [];
  const links = (sources && Array.isArray(sources.links)) ? sources.links : [];
  const evidence = (sources && sources.evidence_snapshot) || '';
  const lw = Array.isArray(linkWarnings) ? linkWarnings : [];
  const fw = Array.isArray(factWarnings) ? factWarnings : [];

  const hasAnything = documents.length || links.length || lw.length || fw.length || evidence;
  if (!hasAnything) return null;

  const warnCount = lw.length + fw.length;

  return (
    <div className="mt-4 rounded-lg border border-[#24374F] bg-[#12233B] overflow-hidden" data-testid="grounding-sources-panel">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#16283F] transition-colors"
        data-testid="grounding-sources-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-slate-200">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          Quellen &amp; Grounding
          <span className="text-xs text-slate-400 font-normal">
            {documents.length} Dokument(e) · {links.length} Link(s)
          </span>
        </span>
        {warnCount > 0 && (
          <span className="flex items-center gap-1 text-xs text-amber-400" title="Erkannte mögliche Halluzinationen">
            <AlertTriangle className="w-3.5 h-3.5" />
            {warnCount}
          </span>
        )}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4 text-sm">
          {/* Dokumente */}
          {documents.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Dokumente</div>
              <ul className="space-y-1">
                {documents.map((d, i) => (
                  <li key={i} className="flex items-center gap-2 text-slate-200">
                    <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <span className="truncate">{d.name}</span>
                    {d.extracted ? (
                      <span className="text-xs text-emerald-400 whitespace-nowrap">✓ {d.chars} Zeichen gelesen</span>
                    ) : (
                      <span className="text-xs text-amber-400 whitespace-nowrap">⚠ kein Text (evtl. gescannt/Bild)</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Links */}
          {links.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Ausgelesene Links</div>
              <ul className="space-y-1">
                {links.map((l, i) => (
                  <li key={i} className="flex items-center gap-2">
                    {l.ok
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                      : <XCircle className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                    <a href={l.url} target="_blank" rel="noopener noreferrer"
                       className="truncate text-[#00D9C8] hover:underline">{l.url}</a>
                    <span className="text-xs whitespace-nowrap text-slate-400">
                      {l.ok ? `${l.chars} Zeichen` : 'nicht verwertbar'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Warnungen: entfernte Links */}
          {lw.length > 0 && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2">
              <div className="flex items-center gap-2 text-amber-300 text-xs font-medium mb-1">
                <LinkIcon className="w-3.5 h-3.5" /> Entfernte, nicht belegte Links
              </div>
              <ul className="list-disc list-inside text-amber-200/90 text-xs space-y-0.5">
                {lw.map((u, i) => <li key={i} className="break-all">{u}</li>)}
              </ul>
            </div>
          )}

          {/* Warnungen: verdächtige Zahlen */}
          {fw.length > 0 && (
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2">
              <div className="flex items-center gap-2 text-amber-300 text-xs font-medium mb-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Nicht belegte Zahlen/Angaben (bitte prüfen)
              </div>
              <ul className="list-disc list-inside text-amber-200/90 text-xs space-y-0.5">
                {fw.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}

          {/* Evidenz-Snapshot */}
          {evidence && (
            <div>
              <button
                type="button"
                onClick={() => setShowEvidence(s => !s)}
                className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"
                data-testid="grounding-evidence-toggle"
              >
                {showEvidence ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Genutzte Grounding-Daten anzeigen
              </button>
              {showEvidence && (
                <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-[#0C1728] border border-[#24374F] p-3 text-xs text-slate-300 whitespace-pre-wrap break-words">
                  {evidence}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
